import { captureException } from "@sentry/react"
import { toast } from "@suraasa/placebo-ui"
import api from "api"
import {
  CpatAssessmentQuestion,
  CpatAttempt,
  QuestionStatus,
} from "api/resources/cpat/types"
import { APIError } from "api/utils"
import { addMilliseconds } from "date-fns"
import { useEffect, useState } from "react"
import { mapErrorsV2 } from "utils/helpers"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"
// import { GA } from "shared/utils/googleAnalytics"

const parseDuration = (duration: string) => {
  const dayStamp = duration.split(" ")
  let seconds = 0
  if (dayStamp.length > 1) {
    seconds = +dayStamp[0] * 24 * 60 * 60
  }
  const timeStamp = dayStamp[dayStamp.length - 1].split(":")
  for (let i = 0; i < timeStamp.length; i++) {
    seconds += +timeStamp[i] * Math.pow(60, timeStamp.length - 1 - i)
  }

  return seconds * 1000
}

export function useCpatAssessment({
  attemptId,
  onInvalidAttempt,
}: {
  onInvalidAttempt: (reason?: string) => void
  attemptId: string
}) {
  const [questionBank, setQuestionBank] = useState<CpatAssessmentQuestion[]>([])
  const [attempt, setAttempt] = useState<CpatAttempt>()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleInvalidAttempt = (reason?: string) => {
    onInvalidAttempt(reason)
  }

  let endTime = ""
  let strictEndTime = ""
  if (attempt) {
    if (attempt.startTime) {
      endTime = addMilliseconds(
        new Date(attempt.startTime),
        parseDuration(attempt.assessment.duration ?? "")
      ).toISOString()
    }
    if (attempt.assessment.endTime)
      strictEndTime = new Date(attempt.assessment.endTime).toISOString()
  }

  /**
   * Duration for which exam will be active for user
   */
  const testDuration = attempt?.assessment.endTimeStrict
    ? endTime > strictEndTime
      ? strictEndTime
      : endTime
    : endTime

  const getAssessmentData = async () => {
    try {
      const res = await api.cpat.getAttempt({
        urlParams: {
          attemptId,
        },
      })
      if ("autoSubmitted" in res) {
        handleInvalidAttempt("Assessment already submitted.")
        return
      }
      if ("responses" in res) {
        // GA.trackEvent("ito_assessment_started", {
        //   assessment_id: res.data.assessment.id,
        //   assessment_type: res.data.assessment.assessmentType,
        // })
        if (res.responses.length === 0) {
          captureException(
            new Error("Assessment questions length 0"),
            scope => {
              scope.setExtras({
                attemptId,
              })
              return scope
            }
          )
          toast.error("We're having trouble getting the questions")
          handleInvalidAttempt()
        }
        setQuestionBank(res.responses)
        setAttempt(res)
      }
    } catch (err) {
      mapErrorsV2(err)
      handleInvalidAttempt()
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const submit = async (options?: { autoSubmitReason?: string }) => {
    setIsSubmitting(true)
    // For testing, don't remove
    // await new Promise(resolve => setTimeout(resolve, 3000))
    // setIsSubmitting(false)
    // return true

    trackingService.trackEvent(
      TrackingEvents.cpatSubmitAPICalled,
      {
        total_questions: questionBank.length,
        attempted_questions: questionBank.filter(
          q => q.status === QuestionStatus.ATTEMPTED
        ).length,
      },
      { services: ["PostHog"] }
    )

    try {
      await api.cpat.submit({
        urlParams: {
          attemptId,
        },
      })
      //   const eventData = options?.autoSubmitReason
      //     ? {
      //         reason: options?.autoSubmitReason,
      //         mode: "auto",
      //       }
      //     : {}
      //   GA.trackEvent("ito_assessment_submitted", {
      //     assessment_id: attempt?.assessment.id,
      //     assessment_type: attempt?.assessment.assessmentType,
      //     mode: "self",
      //     ...eventData,
      //   })
      setIsSubmitting(false)
      return true
    } catch (err) {
      mapErrorsV2(err)
      setIsSubmitting(false)
      return false
    }
  }

  const markAnswer = async (
    question: CpatAssessmentQuestion,
    value: number[]
  ) => {
    let status: QuestionStatus = QuestionStatus.UNREAD
    let response: number[] = []

    if (value.length > 0) {
      status = QuestionStatus.ATTEMPTED
      response = value
    } else {
      status = QuestionStatus.UNATTEMPTED
      response = []
    }

    let originalResponse: number[] | null
    setQuestionBank(qb =>
      qb.map(item => {
        if (item.id === question.id) {
          // @ts-expect-error broken types
          originalResponse = item.response
          return { ...item, status, response }
        }
        return item
      })
    )

    // GA.trackEvent("ito_assessment_mark_answer", {
    //   question_id: question.question.id,
    //   response: value,
    //   assessment_id: attempt?.assessment.id,
    // })

    trackingService.trackEvent(
      TrackingEvents.cpatMarkAnswer,
      {
        question_id: question.id,
        question_sequence: question.sequence,
        response: response,
      },
      { services: ["PostHog", "GA"] }
    )

    try {
      const res = await api.cpat.markAnswer({
        urlParams: {
          questionId: question.id,
        },
        data: {
          status,
          response,
        },
      })
      if (res && "autoSubmitted" in res) {
        toast.error("Assessment already submitted.")
        handleInvalidAttempt()
        return
      }
      return res
    } catch (err) {
      mapErrorsV2(err)
      if (err instanceof APIError) {
        // Revert answer to original state if API fails
        setQuestionBank(qb =>
          qb.map(item => {
            if (item.id === question.id) {
              return { ...item, status, response: originalResponse }
            }
            return item
          })
        )

        if (err.statusCode === 404) {
          handleInvalidAttempt()
        }
      }
    }
  }

  useEffect(() => {
    getAssessmentData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isSubmitting,
    testDuration,
    questionBank,
    submit,
    markAnswer,
    attempt,
  }
}
